import { useCallback, useEffect } from 'react';

import type { Client, PlayerId } from '@suntex.fim/api-client';

import {
  loadAuditoryMemorySounds,
  loadVifBoardSounds,
  playFactAnswer,
  playVifSound,
  stopAllSounds,
  whenSoundQueueIsIdle,
} from '../audio/vifSounds';
import type { RulesVariantConfig } from '../core/rulesVariantConfig';
import type { GridConfig } from '../core/types';

import { useInteractionSeconds } from './useInteractionSeconds';
import type {
  RulesGame,
  RulesPhase,
  UseRulesFamilyGameOptions,
} from './useRulesBoardGame';
import { type UseVifBoard, useVifBoard } from './useVifBoard';
import { type VifProgress, useVifProgress } from './useVifProgress';

/**
 * The phases during which a game is on the clock.
 *
 * A board is offered at `ready` and the player is working from that point on,
 * through the between-layer wipe, until the game is won. `gameOver` is the end
 * screen and `practice` is the round chooser: neither is the task.
 */
const ON_TASK_PHASES: ReadonlySet<RulesPhase> = new Set<RulesPhase>([
  'ready',
  'playing',
  'clearing',
]);

export interface UseVifRulesModuleOptions<TFlag> {
  flag: TFlag;
  /** Board shape. The rules board is 3 rows by `intColumns`, permanently 3; the block board is a fixed 4x4 grid. */
  grid: GridConfig;
  api: Client;
  playerId: PlayerId;
  /** Identifies the module whose progress is read and written. */
  moduleId: number;
  moduleVariant: number;
  /** Preview/admin login: opens on the practice menu and scores nothing. */
  preview?: boolean;
  /** Host's sound directory, e.g. the CDN's `sounds/`. */
  soundDirectory: string;
  /** Called once, from the end screen's Continue on the final game. */
  onComplete: () => void;
  /** Resolves a flag to its pacing config. `addSubVariantConfig`/`mulDivVariantConfig`. */
  resolveConfig: (flag: TFlag) => RulesVariantConfig;
  /** The family's game hook: `useAddSubGame` or `useMulDivGame`. */
  useGame: (options: UseRulesFamilyGameOptions<TFlag>) => RulesGame;
}

export interface VifRulesModule {
  progress: VifProgress;
  game: RulesGame;
  board: UseVifBoard;
  /**
   * Ref for the game surface. Time on task is measured from interaction inside
   * it, so it has to be attached for anything to be counted.
   */
  surfaceRef: (element: HTMLElement | null) => void;
  /** A board is live: tiles are revealed and drags are accepted. */
  playing: boolean;
  onDrop: (from: number, to: number) => void;
  /**
   * Tapping a solved tile speaks its answer, cutting off any answer still
   * speaking. Auditory Memory boards only.
   */
  onSpeak?: (index: number) => void;
}

/**
 * Everything a "3xN rules" VIF module does that is not family-specific:
 * reading and writing the player's position, running the game, owning the
 * board, and measuring time on task against the game's lifecycle.
 *
 * The families differ in their rules, pacing config and problem selection,
 * which is exactly what `resolveConfig` and `useGame` supply. They do not
 * differ in any of the orchestration here, and holding a copy per family meant
 * every lifecycle fix had to be made twice in parallel - the way two copies
 * drift apart.
 */
export function useVifRulesModule<TFlag>({
  flag,
  grid,
  api,
  playerId,
  moduleId,
  moduleVariant,
  preview = false,
  soundDirectory,
  onComplete,
  resolveConfig,
  useGame,
}: UseVifRulesModuleOptions<TFlag>): VifRulesModule {
  const config = resolveConfig(flag);

  const timeOnTask = useInteractionSeconds();

  useEffect(() => {
    loadVifBoardSounds(soundDirectory);
    if (config.spokenEquations) {
      loadAuditoryMemorySounds(soundDirectory);
    }
  }, [soundDirectory, config.spokenEquations]);

  useEffect(() => stopAllSounds, []);

  const progress = useVifProgress({
    api,
    playerId,
    moduleId,
    moduleVariant,
    preview,
    roundsInModule: config.roundsInModule,
    gamesPerRound: config.gamesPerRound,
    timeOnTask,
  });

  const onTimeout = useCallback(() => {
    playVifSound('timeout', { mode: 'interrupt' });
  }, []);

  const onGameWon = useCallback(() => {
    playVifSound('win');
    progress.saveGame();
  }, [progress.saveGame]);

  const game = useGame({
    flag,
    grid,
    round: progress.round,
    game: progress.game,
    practice: progress.practice,
    // Nothing about the position is settled until the read lands, so the game
    // holds off deciding its opening phase from placeholder values.
    loading: progress.loading,
    onGameWon,
    onTimeout,
    onNextGame: progress.advance,
    onChooseRound: progress.selectRound,
    onComplete,
  });

  useEffect(() => {
    if (game.phase === 'practice') stopAllSounds();
  }, [game.phase]);

  const playing = game.phase === 'playing';

  const { boardCleared } = game;
  const onCleared = useCallback(() => {
    if (config.spokenEquations) whenSoundQueueIsIdle(boardCleared);
    else boardCleared();
  }, [config.spokenEquations, boardCleared]);

  const board = useVifBoard({
    config: grid,
    deal: game.deal,
    onCleared,
    onRejectSettled: game.rejectSettled,
  });

  // Start and stop the counter with the game rather than with this component,
  // so the loading gap, the end screen and the practice chooser accrue nothing.
  useEffect(() => {
    timeOnTask.setActive(!progress.loading && ON_TASK_PHASES.has(game.phase));
  }, [timeOnTask, progress.loading, game.phase]);

  // Each save reports the time for one game, so nothing may carry into the
  // next. `saveGame` takes what accrued; this drops whatever the player added
  // between that and the new position landing.
  useEffect(() => {
    timeOnTask.reset();
  }, [timeOnTask, progress.round, progress.game]);

  const onDrop = useCallback(
    (from: number, to: number) => {
      if (!playing) {
        return;
      }

      const outcome = board.drop(from, to);
      if (outcome === null) {
        return;
      }

      if (outcome.rejected) {
        playVifSound('incorrect');
      } else if (outcome.solved.length > 0) {
        if (config.spokenEquations) {
          // Target first, then any bonus cell, one after the other.
          for (const index of outcome.solved) {
            playFactAnswer(game.deal.equations[index] ?? '', {
              mode: 'sequential',
            });
          }
        } else {
          playVifSound('correct');
        }
      }

      if (outcome.complete) {
        game.stopClock();
      }
    },
    [playing, board, config.spokenEquations, game],
  );

  const equations = game.deal.equations;
  const speakAnswer = useCallback(
    (index: number) => {
      playFactAnswer(equations[index] ?? '', { mode: 'interrupt' });
    },
    [equations],
  );

  return {
    progress,
    game,
    board,
    surfaceRef: timeOnTask.surfaceRef,
    playing,
    onDrop,
    onSpeak: config.spokenEquations ? speakAnswer : undefined,
  };
}
